import { useEffect } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty, some } from "lodash/fp";
import { useParams } from "react-router-dom";

import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import { useMeetingReportContext } from "@app/analytics/MeetingReport/providers/MeetingReportContext";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import type {
  Edit,
  RecapMainSection,
  SingleNoteEdit,
  SingleTranscriptEdit,
} from "@app/analytics/MeetingReport/types";
import {
  condenseNoteEdits,
  condenseTranscriptEdits,
  generateNoteApiOperationsFromEdits,
  generateTranscriptApiOperationsFromEdits,
} from "@app/analytics/MeetingReport/utils/editUtils";
import apiClient from "@app/common/api/apiClient";
import { toast } from "@app/common/hooks/useToasts";
import type {
  ApiClipOperation,
  ApiSessionNoteOperation,
  ApiTranscriptOperation,
} from "@app/common/types";

const preventUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

const useEditReport = () => {
  const { sessionId } = useParams() as { sessionId: string };
  const { mainScrollContainer } = useMeetingReportContext();

  const editStore = useEditStore();
  const clearSpeakerFilters = useTranscriptStore(
    (state) => state.clearSpeakerFilters
  );

  const {
    turns,
    summary,
    speakers,
    isEdited: transcriptIsEdited,
    wordDelimiter,
  } = useTranscript();
  const { notes, isNotesEdited } = useMeetingReportSessionNotes();
  const { clearSearch } = useSearch();

  const queryClient = useQueryClient();

  const handleSettled = () => {
    queryClient.invalidateQueries({ queryKey: ["transcript", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["highlights", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["chapters", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["notes", sessionId] });

    // close any modals that were open
    editStore.setModal(null);
  };

  const handleSuccess = () => {
    exitEditMode();
    toast.success("Your changes have been saved");
  };

  const updateTranscriptMutation = useMutation({
    mutationFn: (operations: ApiTranscriptOperation[]) =>
      apiClient.session.patchTranscript(sessionId, operations),
    onSettled: handleSettled,
    onSuccess: handleSuccess,
    onError: () => {
      toast.error({
        type: "system",
        title: "Error saving changes",
        // TODO (ENG-10988): Display error message from API
        message: "Read was not able to save changes.",
      });
    },
  });

  const restoreTranscriptMutation = useMutation({
    mutationFn: apiClient.session.restoreTranscript,
    onSettled: handleSettled,
    onSuccess: () => {
      Events.revertToOriginal({ section: "transcript" });
    },
  });

  const restoreNotesMutation = useMutation({
    mutationFn: apiClient.session.restoreNotes,
    onSettled: handleSettled,
    onSuccess: () => {
      Events.revertToOriginal({ section: "notes" });
    },
  });

  const updateNotesMutation = useMutation({
    mutationFn: async ({
      clipOperations,
      noteOperations,
    }: {
      clipOperations: ApiClipOperation[];
      noteOperations: ApiSessionNoteOperation[];
    }) => {
      const results = await Promise.allSettled([
        apiClient.clips.patchAll(clipOperations),
        apiClient.session.notes.patchNotes(sessionId, noteOperations),
      ]);
      // throw an error if either of the updates fail
      if (some((r) => r.status !== "fulfilled", results)) {
        throw new Error("Error updating clips or notes");
      }
    },
    onSettled: handleSettled,
    onSuccess: handleSuccess,
    onError: () => {
      toast.error({
        type: "system",
        title: "Error saving changes",
        // since we have to do two calls (one to update the clips and one to update the notes), it's possible that one query succeeded but the other failed
        message: "One or more of your changes could not be saved.",
      });
    },
  });

  const enterEditMode = (section: RecapMainSection) => {
    Events.startEditing({ section });
    clearSearch();

    if (section === "transcript") {
      editStore.startEditSession({ section, data: { turns, speakers } });
      clearSpeakerFilters();
    } else {
      editStore.startEditSession({
        section,
        data: { ...summary, notes },
      });
    }
  };

  const exitEditMode = () => {
    const resetScrollPosition = editStore.editingSection === "notes";
    editStore.reset();
    clearSearch();
    if (resetScrollPosition && mainScrollContainer.current) {
      // since we expand everything when the notes are being edited, reset the
      // scroll position when exiting edit mode so that the user isn't stuck
      // looking at a blank page (unrendered virtuoso transcript)
      mainScrollContainer.current.scrollTop = 0;
    }
  };

  const hasEdits = (condensedEdits: Edit[]) => {
    return !isEmpty(condensedEdits);
  };

  const getCondensedEdits = () => {
    if (editStore.editingSection === "transcript") {
      return condenseTranscriptEdits(editStore.transcript.edits, wordDelimiter);
    }
    return condenseNoteEdits(editStore.notes.edits);
  };

  const handleCancelChanges = (force = false) => {
    if (force) {
      return exitEditMode();
    }

    const condensedEdits = getCondensedEdits();

    if (hasEdits(condensedEdits)) {
      // if the user has unsaved edits but is trying to exit edit mode,
      // confirm whether they would indeed like to discard their changes
      editStore.setModal("confirm_cancel_changes");
    } else {
      exitEditMode();
    }
  };

  const handleSaveChanges = () => {
    Events.saveChanges();

    const condensedEdits = getCondensedEdits();

    if (!hasEdits(condensedEdits)) {
      exitEditMode();
      return;
    }

    if (editStore.editingSection === "transcript") {
      const operations = generateTranscriptApiOperationsFromEdits(
        condensedEdits as SingleTranscriptEdit[]
      );
      updateTranscriptMutation.mutate(operations);
    } else {
      const operations = generateNoteApiOperationsFromEdits(
        condensedEdits as SingleNoteEdit[],
        sessionId
      );
      updateNotesMutation.mutate(operations);
    }
  };

  useEffect(() => {
    if (editStore.editingSection == null) {
      window.removeEventListener("beforeunload", preventUnload);
    } else {
      window.addEventListener("beforeunload", preventUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", preventUnload);
    };
  }, [editStore.editingSection]);

  return {
    enterEditMode,

    isSaving:
      updateTranscriptMutation.isPending || updateNotesMutation.isPending,
    onSaveChanges: handleSaveChanges,
    onCancelChanges: handleCancelChanges,
    canRestoreNotes: summary.editing.isEdited || isNotesEdited,
    restoreNotes: restoreNotesMutation,
    canRestoreTranscript: transcriptIsEdited,
    restoreTranscript: restoreTranscriptMutation,
  };
};

export default useEditReport;
